import React, { CSSProperties } from 'react';
import { areEqual } from 'react-window';

import ListRowFrame from 'flow/components/nav/list-keys/ListRowFrame';
import {
    RowStore,
    useStoreHoldsRow,
} from 'flow/components/nav/list-keys/row-index-store';
import { ChatListRow, chatListRowKey, rowIsFocusable } from './chat-list-rows';
import ChatSessionListItem from './ChatSessionListItem';
import ChatSessionSkeletonItem from './ChatSessionSkeletonItem';
import MeetingsListItem from './MeetingsListItem';
import NewChatListItem from './NewChatListItem';
import NewChatSectionTitle from './NewChatSectionTitle';
import NoChatSessions from './NoChatSessions';
import OverviewListItem from './OverviewListItem';
import SmsChatListItem from './SmsChatListItem';

export interface ChatListItemData {
    rows: ChatListRow[];
    tabStopStore: RowStore<string>;
    /** One key for each row, from uniqueRowKeys. */
    rowKeys: string[];
    onRowUnmountWithFocus: () => void;
    /** Holds the key of the open page's row: a chat or the meetings. */
    openRowStore: RowStore<string>;
}

interface Props {
    index: number;
    style: CSSProperties;
    data: ChatListItemData;
}

const RowContent = ({ index, data }: Omit<Props, 'style'>) => {
    const row = data.rows[index];
    const rowIsOpen = useStoreHoldsRow(data.openRowStore, chatListRowKey(row));

    switch (row.kind) {
        case 'overview':
            return <OverviewListItem isOpen={rowIsOpen} />;
        case 'meetings':
            return <MeetingsListItem isOpen={rowIsOpen} />;
        case 'chatSession':
            return (
                <ChatSessionListItem
                    chatSessionKey={row.chatSessionKey}
                    isOpen={rowIsOpen}
                />
            );
        case 'noChatSessions':
            return <NoChatSessions />;
        case 'skeleton':
            return <ChatSessionSkeletonItem />;
        case 'newChatTitle':
            return <NewChatSectionTitle />;
        case 'newChat':
            return <NewChatListItem extensionKey={row.extensionKey} />;
        case 'sms':
            return <SmsChatListItem searchText={row.searchText} />;
        default:
            return null;
    }
};

const ChatListWindowRow = ({ index, style, data }: Props) => (
    <ListRowFrame
        index={index}
        style={style}
        isFocusable={rowIsFocusable(data.rows[index])}
        tabStopStore={data.tabStopStore}
        tabStopKey={data.rowKeys[index]}
        onUnmountWithFocus={data.onRowUnmountWithFocus}
    >
        <RowContent index={index} data={data} />
    </ListRowFrame>
);

export default React.memo(ChatListWindowRow, areEqual);
